import { FloorZone } from '../types';

/**
 * Station names, as three different systems spell them, onto zones of a plan.
 *
 * `listStaffTracking` keys its per-station figures by a free string that the CV
 * job writes, and nothing constrains it to the `CookingStationName` enum KDS
 * uses elsewhere — records in the wild carry "Butchery", "butchery" and
 * "Butchery Section" for the same room. Matching is therefore done on a
 * normalised form rather than by equality, and an unmatched station is reported
 * as unmatched rather than dropped: a station nobody can place is a mapping bug
 * worth seeing, and silently discarding it would hide work the kitchen did.
 *
 * A camera's calibrated room takes the same route (`withCameras` in `plan.ts`).
 *
 * The table is Bahrain's station list; other kitchens match on the zone names
 * in their own plan file, which is what the fallback below is for.
 */

const normalise = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * Station spelling → zone id. Keys are normalised on the way in, so
 * "Garde Manger", "gardeManger" and "garde-manger" are the same entry.
 */
const STATION_ALIASES: Record<string, string> = {
  // CookingStationName, as KDS spells it elsewhere.
  butchery: 'butchery-section',
  vegetable: 'vegetable-section',
  hot: 'hot-kitchen',
  boiling: 'hot-kitchen',
  roasting: 'roasting-section',
  sauce: 'sauce-section',
  saucier: 'sauce-section',
  coldsauce: 'hot-sauce',
  gardemanger: 'cold-kitchen',
  coldpastry: 'cake-prep',
  bakery: 'cake-prep',
  bakeryandpastry: 'cake-prep',
  hotbakery: 'cake-prep',
  portioning: 'portioning-room',
  assembly: 'portioning-room',
  commissary: 'dry-store',
  merchandise: 'customs-kids',

  // Room names as camera_rooms.json records them, for records written from the
  // CV side rather than from the kitchen's own station list.
  butcherysection: 'butchery-section',
  vegetablesection: 'vegetable-section',
  vegetablessection: 'vegetable-section',
  hotkitchen: 'hot-kitchen',
  hotkitchensection: 'hot-kitchen',
  hotsection: 'hot-kitchen',
  hotsauce: 'hot-sauce',
  hotsaucesection: 'hot-sauce',
  roastingsection: 'roasting-section',
  coldsection: 'cold-kitchen',
  coldkitchen: 'cold-kitchen',
  coldkitchensection: 'cold-kitchen',
  saucesection: 'sauce-section',
  portioningroom: 'portioning-room',
  portioningroom1: 'portioning-room',
  portioningroom2: 'portioning-room',
  cakepreparationarea: 'cake-prep',
  potwashing: 'pot-washing',
  potwashingsection: 'pot-washing',
  fruitsvegetableswashing: 'fv-washing',
  fruitvegetableswashingandsanitationstation: 'fv-washing',
  drystore: 'dry-store',
  kitchenentrance: 'entrance-lobby',
  componentchiller2: 'walkin-chiller-2',
  customskidssection: 'customs-kids',
  cafesection: 'cafe-section'
};

/** The zone a station name refers to, or null if nothing on this plan matches. */
export const zoneForStation = (zoneById: Map<string, FloorZone>, station: string): string | null => {
  const key = normalise(station);
  const direct = STATION_ALIASES[key];
  if (direct && zoneById.has(direct)) {
    return direct;
  }
  // Fall back to the zone names themselves, so a zone added to a plan file is
  // matchable without also being added to the table above.
  for (const [id, zone] of zoneById) {
    if (normalise(zone.name) === key || normalise(id) === key) {
      return id;
    }
  }
  return null;
};
